import { type FacilitiesSearchQueryInput, type FacilitySearchResult } from "../queries";

export type SectionReference = {
  id: string;
  name: string;
  legacyId: string | null;
};

export type SectionSuggestion = {
  section: SectionReference;
  facilityCount: number;
  exactZipCount: number;
  closestZip: string;
  closestZipDistance: number;
  examples: FacilitySearchResult[];
  source: "postal_mapping" | "postal_mapping_with_facility_evidence" | "facility_evidence";
};

const NEARBY_ZIP_OFFSETS = [-2, -1, 1, 2];

export function normalizeZipCode(value: string | null | undefined): string {
  const match = (value || "").match(/\d{5}/);
  return match?.[0] || "";
}

function formatZipCode(value: number): string {
  return value.toString().padStart(5, "0");
}

export function buildSectionSuggestionSearchInputs(zipCode: string): FacilitiesSearchQueryInput[] {
  const normalizedZip = normalizeZipCode(zipCode);
  if (!normalizedZip) return [];

  const zipNumber = Number(normalizedZip);
  const nearbyZipCodes = NEARBY_ZIP_OFFSETS.map((offset) => zipNumber + offset)
    .filter((value) => value >= 0 && value <= 99999)
    .map(formatZipCode);

  return Array.from(new Set([normalizedZip, ...nearbyZipCodes, normalizedZip.slice(0, 3)])).map((postalCode) => ({
    postalCodes: [postalCode],
    excludeArchived: true
  }));
}

function zipDistance(baseZip: string, candidateZip: string): number {
  return Math.abs(Number(baseZip) - Number(candidateZip));
}

function facilitySortKey(baseZip: string, facility: FacilitySearchResult): string {
  const candidateZip = normalizeZipCode(facility.mainAddress?.postalCode);
  const distance = candidateZip ? zipDistance(baseZip, candidateZip) : Number.MAX_SAFE_INTEGER;
  return `${distance.toString().padStart(5, "0")}-${facility.name}`;
}

export function buildSectionSuggestions({
  zipCode,
  facilities,
  mappedSection
}: {
  zipCode: string;
  facilities: FacilitySearchResult[];
  mappedSection?: SectionReference | null;
}): SectionSuggestion[] {
  const normalizedZip = normalizeZipCode(zipCode);
  if (!normalizedZip) return [];

  const facilitiesById = new Map<string, FacilitySearchResult>();

  facilities.forEach((facility) => {
    const facilityZip = normalizeZipCode(facility.mainAddress?.postalCode);
    if (!facility.section || !facilityZip) return;

    const isSameZipPrefix = facilityZip.slice(0, 3) === normalizedZip.slice(0, 3);
    if (!isSameZipPrefix && zipDistance(normalizedZip, facilityZip) > 2) return;

    facilitiesById.set(facility.id, facility);
  });

  const groupedFacilities = new Map<string, FacilitySearchResult[]>();

  Array.from(facilitiesById.values()).forEach((facility) => {
    const sectionId = facility.section?.id;
    if (!sectionId) return;

    groupedFacilities.set(sectionId, [...(groupedFacilities.get(sectionId) || []), facility]);
  });

  const facilitySuggestions = Array.from(groupedFacilities.values())
    .map((sectionFacilities) => {
      const [firstFacility] = sectionFacilities;
      const section = firstFacility?.section;
      if (!section) return null;

      const sortedFacilities = [...sectionFacilities].sort((left, right) =>
        facilitySortKey(normalizedZip, left).localeCompare(facilitySortKey(normalizedZip, right))
      );
      const facilityDistances = sortedFacilities.map((facility) => {
        const facilityZip = normalizeZipCode(facility.mainAddress?.postalCode);
        return {
          zipCode: facilityZip,
          distance: zipDistance(normalizedZip, facilityZip)
        };
      });
      const closest = facilityDistances.reduce((best, candidate) =>
        candidate.distance < best.distance ? candidate : best
      );

      const suggestion: SectionSuggestion = {
        section,
        facilityCount: sortedFacilities.length,
        exactZipCount: sortedFacilities.filter(
          (facility) => normalizeZipCode(facility.mainAddress?.postalCode) === normalizedZip
        ).length,
        closestZip: closest.zipCode,
        closestZipDistance: closest.distance,
        examples: sortedFacilities.slice(0, 3),
        source: "facility_evidence" as const
      };
      return suggestion;
    })
    .filter((suggestion): suggestion is SectionSuggestion => suggestion !== null)
    .sort((left, right) => {
      if (left.closestZipDistance !== right.closestZipDistance) {
        return left.closestZipDistance - right.closestZipDistance;
      }
      if (left.exactZipCount !== right.exactZipCount) {
        return right.exactZipCount - left.exactZipCount;
      }
      if (left.facilityCount !== right.facilityCount) {
        return right.facilityCount - left.facilityCount;
      }
      return left.section.name.localeCompare(right.section.name);
    });

  if (!mappedSection) return facilitySuggestions;

  const mappedFacilitySuggestion = facilitySuggestions.find((suggestion) => suggestion.section.id === mappedSection.id);
  const mappedSuggestion: SectionSuggestion = {
    section: mappedSection,
    facilityCount: mappedFacilitySuggestion?.facilityCount || 0,
    exactZipCount: mappedFacilitySuggestion?.exactZipCount || 0,
    closestZip: mappedFacilitySuggestion?.closestZip || normalizedZip,
    closestZipDistance: mappedFacilitySuggestion?.closestZipDistance || 0,
    examples: mappedFacilitySuggestion?.examples || [],
    source: mappedFacilitySuggestion ? "postal_mapping_with_facility_evidence" : "postal_mapping"
  };

  return [mappedSuggestion, ...facilitySuggestions.filter((suggestion) => suggestion.section.id !== mappedSection.id)];
}

export function shouldShowAlternateSectionSuggestion(suggestions: SectionSuggestion[]): boolean {
  const alternate = suggestions[1];
  if (!alternate) return false;

  return alternate.exactZipCount > 0 || alternate.closestZipDistance <= 2;
}
